import React, { useEffect, useState } from "react";

// 1. 引入数据和主题管理
import { getText } from "./data/content";
import { theme, combineClasses, getDefaultTheme } from "./theme/theme";

// 2. 引入组件
import QuantumBackground from "./components/QuantumBackground";
import Modal from "./components/Modal";

// 3. 引入页面组件（由 AppRoutes 统一渲染）
import ContactView from "./pages/contact";

// 4. 拆分后的布局/导航组件
import SiteNavbar from "./components/nav/SiteNavbar";
import AppRoutes from "./components/layout/AppRoutes";
import AppFooter from "./components/layout/AppFooter";

// （可选）滚动锁定 Hook：用于移动菜单打开时防止滚动穿透
import { useBodyScrollLock } from "./hooks/useBodyScrollLock";

/**
 * ============================================================================
 * ############## App：全站顶层编排 ############################################
 * ============================================================================
 *
 * 【作用】（第三人称客观注释）
 * - 负责全站状态（语言/主题/当前页面/模态框/移动菜单）与全局副作用（滚动归顶、主题持久化）。
 * - 导航条、路由渲染、页脚等 UI 由子组件负责；App 仅负责“数据与事件流”的组织。
 *
 * 【拆分原则】（第三人称客观注释）
 * - 具备“纯展示/可复用”的部分下沉为组件（Navbar、Footer、Routes、NavButton）。
 * - 具备“全局状态/副作用”的部分保留在 App（theme/lang/page/modal）。
 * - 事件通过 props 由 App 下发，避免子组件隐式依赖全局。
 */
const App = () => {
  /**
   * ============================================================================
   * ############## 01. 全站状态 ###############################################
   * ============================================================================
   */
  const [activePage, setActivePage] = useState("home");
  const [lang, setLang] = useState<"en" | "cn">("en");

  // 导航：移动端菜单开关状态
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // 模态框：当前打开的 Modal 类型（此处仅保留 contact；可按需扩展）
  const [modalPage, setModalPage] = useState<"contact" | null>(null);

  // 主题模式：从 localStorage 读取或根据时间设置默认值
  const [themeMode, setThemeMode] = useState<"light" | "dark">(() => {
    // 只在客户端读取 localStorage，避免 SSR/预渲染环境报错
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("themeMode");
      if (saved === "light" || saved === "dark") return saved;
    }
    return getDefaultTheme();
  });

  const isLight = themeMode === "light";

  /**
   * ============================================================================
   * ############## 02. 导航项定义（集中管理） ##################################
   * ============================================================================
   *
   * 【作用】（第三人称客观注释）
   * - 统一导航项数组，避免桌面/移动端重复写两份 map 逻辑。
   * - 子组件只负责渲染，不负责决定“有哪些页面”。
   */
  const navPages = ["home", "research", "publications", "teaching", "team", "news", "openings"] as const;

  /**
   * ============================================================================
   * ############## 03. 页面切换：滚动归顶 #####################################
   * ============================================================================
   *
   * 【说明】（第三人称客观注释）
   * - activePage 改变时滚动回到顶部，保证页面切换体验一致。
   */
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activePage]);

  /**
   * ============================================================================
   * ############## 04. 主题持久化与全局样式 ###################################
   * ============================================================================
   *
   * 【作用】（第三人称客观注释）
   * - 保存主题偏好到 localStorage。
   * - 写入 documentElement 的主题 class，用于全局 CSS/背景等联动。
   * - 设置 body 背景色作为兜底，避免透明层叠导致的“露底色”不一致。
   */
  useEffect(() => {
    if (typeof window === "undefined") return;

    localStorage.setItem("themeMode", themeMode);

    if (themeMode === "light") {
      document.documentElement.classList.add("light-theme");
      document.documentElement.classList.remove("dark-theme");
      document.body.style.backgroundColor = "#f9fafb";
    } else {
      document.documentElement.classList.add("dark-theme");
      document.documentElement.classList.remove("light-theme");
      document.body.style.backgroundColor = "#080808";
    }
  }, [themeMode]);

  /**
   * ============================================================================
   * ############## 05. 移动端菜单：滚动锁定 + 状态复位 ##########################
   * ============================================================================
   *
   * 【说明】（第三人称客观注释）
   * - 移动菜单打开时锁定 body 滚动，避免滚动穿透造成的交互不稳定。
   * - 页面切换/语言切换时自动关闭移动菜单，避免状态残留。
   */
  useBodyScrollLock(isMenuOpen);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [activePage, lang]);

  /**
   * ============================================================================
   * ############## 06. 事件处理（下发给子组件） ################################
   * ============================================================================
   */
  const toggleLang = () => setLang((l) => (l === "en" ? "cn" : "en"));
  const toggleTheme = () => setThemeMode((t) => (t === "light" ? "dark" : "light"));

  const openContactModal = () => setModalPage("contact");
  const closeModal = () => setModalPage(null);

  return (
    <div
      className={combineClasses(
        isLight ? theme.backgrounds.mainLight : theme.backgrounds.main,
        "flex flex-col min-h-screen"
      )}
    >
      {/* 背景组件：为全站提供统一视觉背景（与主题联动） */}
      <QuantumBackground themeMode={themeMode} />

      {/* ############################################################################
          ############## NAVBAR（全站导航栏）
          - 导航项间距随视口变化（不写死），小屏更紧、大屏更宽；<lg 时收为汉堡下拉。
          - 联系我们与 EN/CN 按钮已还原为较大尺寸。修改方式见 SiteNavbar.tsx / NavButton.tsx 顶部注释。
          ############################################################################ */}
      <SiteNavbar
        lang={lang}
        themeMode={themeMode}
        isMenuOpen={isMenuOpen}
        activePage={activePage}
        navPages={navPages}
        onToggleMenu={() => setIsMenuOpen((v) => !v)}
        onCloseMenu={() => setIsMenuOpen(false)}
        onNavigate={(page) => {
          setActivePage(page);
          // 说明：此处保留“切页同时关闭模态框”的行为，避免页面与模态叠加
          closeModal();
        }}
        onToggleLang={toggleLang}
        onToggleTheme={toggleTheme}
        onOpenContact={openContactModal}
      />

      {/* ############################################################################
          ############## MAIN（页面内容容器） ######################################
          ############################################################################ */}
      <main
        className={combineClasses(
          "relative z-0 pb-8",
          /**
           * [顶端留白说明]（第三人称客观注释）
           * - pt-20：为固定高度导航栏预留空间，防止内容被导航覆盖。
           * - 若 theme.nav.height 不是 h-20（80px），应同步调整此处 pt-*，保持严格一致。
           */
          "pt-20"
        )}
      >
        <AppRoutes
          activePage={activePage}
          lang={lang}
          themeMode={themeMode}
          onChangePage={setActivePage}
        />
      </main>

      {/* ############################################################################
          ############## MODAL（全站模态框） #######################################
          ############################################################################ */}
      {/* 联系我们弹窗：标题用大字号（titleSize="lg"），下方小字为 contact.subtitle；文案见 content-contact.js */}
      <Modal
        isOpen={modalPage === "contact"}
        onClose={closeModal}
        title={getText("contact.title", lang)}
        subtitle={getText("contact.subtitle", lang)}
        titleSize="lg"
        size="xl"
        themeMode={themeMode}
      >
        <ContactView
          lang={lang}
          themeMode={themeMode}
          onChangePage={(page) => {
            setActivePage(page);
            closeModal();
          }}
        />
      </Modal>

      {/* ############################################################################
          ############## FOOTER（页脚，仅 Home 展示） ##############################
          ############################################################################ */}
      {activePage === "home" && <AppFooter lang={lang} themeMode={themeMode} />}
    </div>
  );
};

export default App;
